import React, { useMemo, useState, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Table, Button, Dropdown } from "react-bootstrap";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FaSort, FaSortUp, FaSortDown, FaGripVertical, FaColumns, FaUndo } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import Utils from "../../utils";
import Paging from "./Paging";
import NoResults from "./NoResults";
import SkeletonTable from "./SkeletonTable";
import ActionDropdown from "./ActionDropdown";
import MobileCardGrid from "./MobileCardGrid";
import useGridPreferences from "../../hooks/useGridPreferences";

// Draggable header cell
function DraggableHeader({ header, children, isResizable }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: header.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    position: "relative",
    cursor: "grab",
    whiteSpace: "nowrap",
  };

  return (
    <th ref={setNodeRef} style={style}>
      <div className="d-flex align-items-center">
        <span {...attributes} {...listeners} className="me-1" style={{ cursor: "grab" }}>
          <FaGripVertical size={10} className="text-muted" />
        </span>
        <span style={{ flex: 1 }}>{children}</span>
      </div>
      {isResizable && (
        <div
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          className={`resizer ${header.column.getIsResizing() ? "isResizing" : ""}`}
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            height: "100%",
            width: "4px",
            cursor: "col-resize",
            userSelect: "none",
            touchAction: "none",
            background: header.column.getIsResizing() ? "#cf7f00" : "transparent",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#cf7f00"; }}
          onMouseLeave={(e) => {
            if (!header.column.getIsResizing()) e.currentTarget.style.background = "transparent";
          }}
        />
      )}
    </th>
  );
}

const EnhancedGrid = ({
  gridId,
  data,
  displayColumns,
  idColumn,
  idColumn2,
  actions,
  pageNumber,
  totalPages,
  loading,
  placeHolderLinesCount,
  onPageChange,
  onSort,
  initialOrderBy = -1,
  initialOrderDirection = 1,
  selectedRow,
  selectedRunbook, // backward compat alias
  onRowClick,
  useMobileCards = false,
  actionBar,
  emptyState,
  maxHeight = "600px",
  // Opt-in: instead of a fixed maxHeight scroll region, grow to fill whatever flex
  // space the parent gives (e.g. a resizable DesktopWindow) via flex:1 + min-height:0.
  // Standalone-page callers don't pass this, so their layout is unaffected.
  fillHeight = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  // initialOrderBy/initialOrderDirection let a caller seed the sort indicator
  // from a remembered/URL sort; defaults preserve the unsorted state.
  const [orderBy, setOrderBy] = useState(initialOrderBy);
  const [orderDirection, setOrderDirection] = useState(initialOrderDirection);

  const selected = selectedRow || selectedRunbook;

  const allColumns = useMemo(
    () => displayColumns ?? Object.keys(data[0] || {}),
    [displayColumns, data]
  );

  const {
    columnOrder,
    columnWidths,
    hiddenColumns,
    setColumnOrder,
    setColumnWidth,
    toggleColumnVisibility,
    resetPreferences,
  } = useGridPreferences(gridId, allColumns);

  // Filter out hidden and "support" from visible columns, preserving order.
  // Columns present in allColumns but missing from a saved columnOrder (e.g. a
  // newly-added column for a user with older stored preferences) are appended
  // at the end so they still render instead of silently disappearing.
  const visibleColumns = useMemo(() => {
    const ordered = columnOrder.filter(
      (c) => c !== "support" && !hiddenColumns.includes(c) && allColumns.includes(c)
    );
    const missing = allColumns.filter(
      (c) => c !== "support" && !hiddenColumns.includes(c) && !columnOrder.includes(c)
    );
    return [...ordered, ...missing];
  }, [columnOrder, hiddenColumns, allColumns]);

  // TanStack column defs
  const tableColumns = useMemo(() => {
    return visibleColumns.map((col) => ({
      id: col,
      accessorKey: col,
      header: Utils.fixCamelCase(col),
      cell: ({ row }) => row.original[col],
      size: columnWidths[col] || 150,
      minSize: 60,
    }));
  }, [visibleColumns, columnWidths]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: "onChange",
    state: {
      columnOrder: visibleColumns,
    },
    onColumnOrderChange: setColumnOrder,
  });

  const handleSortClick = (column) => {
    const orderByColumn = Utils.fixCamelCase(column).replaceAll(" ", "");
    let orderDirectionResult;
    if (orderByColumn === orderBy) {
      orderDirectionResult = orderDirection * -1;
    } else {
      orderDirectionResult = 1;
    }
    setOrderBy(orderByColumn);
    setOrderDirection(orderDirectionResult);
    onSort(orderByColumn, orderDirectionResult === 1 ? "Asc" : "Desc", location, navigate);
  };

  // Save column width after resize
  const handleColumnResize = useCallback(() => {
    const headers = table.getFlatHeaders();
    headers.forEach((header) => {
      const newWidth = header.getSize();
      if (newWidth && columnWidths[header.id] !== newWidth) {
        setColumnWidth(header.id, newWidth);
      }
    });
  }, [table, columnWidths, setColumnWidth]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = ({ active, over }) => {
    if (over && active.id !== over.id) {
      const oldIndex = visibleColumns.indexOf(active.id);
      const newIndex = visibleColumns.indexOf(over.id);
      const newOrder = arrayMove(visibleColumns, oldIndex, newIndex);
      // Preserve hidden columns in the full order
      const fullOrder = [
        ...newOrder,
        ...allColumns.filter((c) => !newOrder.includes(c)),
      ];
      setColumnOrder(fullOrder);
    }
  };

  const getSortIcon = (columnId) => {
    const colName = Utils.fixCamelCase(columnId).replaceAll(" ", "");
    if (colName !== orderBy) return <FaSort className="ms-1 mb-1 text-primary" size={10} />;
    if (orderDirection === 1) return <FaSortUp className="ms-1 mb-1 text-primary" size={10} />;
    return <FaSortDown className="ms-1 mb-1 text-primary" size={10} />;
  };

  // Has "support" column?
  const hasSupportColumn = allColumns.includes("support");

  const fillHeightStyle = { display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 };

  return (
    <div style={fillHeight ? fillHeightStyle : undefined}>
      {loading && <SkeletonTable columns={displayColumns?.length || 5} rows={placeHolderLinesCount ?? 8} />}
      {!loading && (
        <div style={fillHeight ? fillHeightStyle : undefined}>
          {totalPages > 0 && (
            <>
              {/* Toolbar */}
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex gap-2">
                  {actionBar}
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <Dropdown>
                    <Dropdown.Toggle variant="outline-secondary" size="sm">
                      <FaColumns className="me-1" /> Columns
                    </Dropdown.Toggle>
                    <Dropdown.Menu style={{ maxHeight: "300px", overflowY: "auto" }}>
                      {allColumns
                        .filter((c) => c !== "support")
                        .map((col) => (
                          <Dropdown.Item
                            key={col}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleColumnVisibility(col);
                            }}
                          >
                            <span className={hiddenColumns.includes(col) ? "" : "fw-bold"}>{Utils.fixCamelCase(col)}</span>
                          </Dropdown.Item>
                        ))}
                      <Dropdown.Divider />
                      <Dropdown.Item onClick={resetPreferences}>
                        <FaUndo className="me-1" /> Reset to Default
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>

              {/* Mobile Card View */}
              {useMobileCards && (
                <MobileCardGrid
                  data={data}
                  displayColumns={visibleColumns}
                  idColumn={idColumn}
                  idColumn2={idColumn2}
                  selectedRunbook={selected}
                  onRowClick={onRowClick}
                />
              )}

              {/* Desktop Table View */}
              <div
                className={useMobileCards ? "d-none d-md-block" : ""}
                style={
                  fillHeight
                    ? { flex: "1 1 auto", minHeight: 0, overflowX: "auto", overflowY: "auto" }
                    : { maxHeight, overflowX: "auto", overflowY: "auto" }
                }
              >
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <Table
                    hover
                    striped
                    style={{ width: "100%", tableLayout: "fixed" }}
                    onMouseUp={handleColumnResize}
                  >
                    <colgroup>
                      {table.getFlatHeaders().map((header) => (
                        <col key={header.id} style={{ width: header.getSize() }} />
                      ))}
                      {actions && <col style={{ width: 50 }} />}
                      {hasSupportColumn && <col style={{ width: 80 }} />}
                    </colgroup>
                    <thead>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                          <SortableContext
                            items={visibleColumns}
                            strategy={horizontalListSortingStrategy}
                          >
                            {headerGroup.headers.map((header) => (
                              <DraggableHeader
                                key={header.id}
                                header={header}
                                isResizable={true}
                              >
                                {onSort ? (
                                  <Button
                                    variant="link"
                                    className="text-decoration-none text-dark ps-0 fw-bold p-0"
                                    onClick={() => handleSortClick(header.id)}
                                    style={{ fontSize: "inherit" }}
                                  >
                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                    {getSortIcon(header.id)}
                                  </Button>
                                ) : (
                                  flexRender(header.column.columnDef.header, header.getContext())
                                )}
                              </DraggableHeader>
                            ))}
                          </SortableContext>
                          {actions && <th style={{ width: "50px" }}></th>}
                          {hasSupportColumn && <th style={{ width: "80px" }}></th>}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.map((row) => {
                        const item = row.original;
                        const isSelected =
                          selected &&
                          selected[idColumn] === item[idColumn] &&
                          (!idColumn2 || selected[idColumn2] === item[idColumn2]);

                        return (
                          <tr
                            key={row.id}
                            className={`${isSelected ? "table-primary" : ""} ${onRowClick ? "cursor-pointer" : ""}`}
                            onClick={() => onRowClick && onRowClick(item)}
                            style={onRowClick ? { cursor: "pointer", userSelect: "none" } : {}}
                          >
                            {row.getVisibleCells().map((cell) => (
                              <td
                                key={cell.id}
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            ))}
                            {actions && (
                              <td className="text-nowrap">
                                <ActionDropdown
                                  actions={actions}
                                  item={item}
                                  idColumn={idColumn}
                                  idColumn2={idColumn2}
                                />
                              </td>
                            )}
                            {hasSupportColumn && <td>{item.support}</td>}
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </DndContext>
              </div>

              {totalPages > 1 && (
                <Paging
                  currentPage={pageNumber}
                  totalPages={totalPages}
                  onPageChange={onPageChange}
                />
              )}
            </>
          )}
        </div>
      )}
      {!loading && totalPages === 0 && (
        <div className="d-flex justify-content-center">
          <NoResults {...(emptyState || {})} />
        </div>
      )}
    </div>
  );
};

export default EnhancedGrid;
